
import { useState } from "react"
import { Button } from "@/components/ui/button"
import { ArrowLeft } from "lucide-react"
import { ImageScanner } from "@/components/image-scanner"
import { StatementTransactions } from "@/components/statement-transactions"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { toast } from "sonner"
import { useAuth } from "@/lib/auth/auth-context"
import { useTransactions } from "@/lib/context/transaction-context"
import { supabase } from "@/integrations/supabase/client"

interface SmartScannerProps {
  onBack: () => void
}

export function SmartScanner({ onBack }: SmartScannerProps) {
  const { user } = useAuth()
  const { refreshTransactions } = useTransactions()

  // Receipt state
  const [scannedReceipt, setScannedReceipt] = useState<any>(null)
  const [amount, setAmount] = useState("")
  const [description, setDescription] = useState("")
  const [category, setCategory] = useState("")
  const [type, setType] = useState<"expense" | "income">("expense")
  const [date, setDate] = useState("")
  const [isSaving, setIsSaving] = useState(false)

  // Statement state
  const [statementTransactions, setStatementTransactions] = useState<any[]>([])
  const [showStatementReview, setShowStatementReview] = useState(false)

  const expenseCategories = [
    "Food", "Transport", "Shopping", "Entertainment", "Health", "Bills", "Other"
  ]

  const incomeCategories = [
    "Freelance", "Business", "Investment", "Bonus", "Gift", "Other"
  ]

  const handleDataParsed = (result: any) => {
    // result = { documentType: "receipt" | "statement", data: {...} }
    if (result.documentType === "receipt") {
      // Single receipt
      const data = result.data
      setScannedReceipt(data)
      setAmount(data.amount || "")
      setDescription(data.description || "")
      setCategory(data.category || "")
      setType(data.type || "expense")
      setDate(data.date || new Date().toISOString().split("T")[0])
    } else {
      // Bank statement with multiple transactions
      const data = result.data
      if (data.transactions && data.transactions.length > 0) {
        setStatementTransactions(data.transactions)
        setShowStatementReview(true)
      }
    }
  }

  const handleSaveReceipt = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error("Please enter a valid amount")
      return
    }

    if (!category) {
      toast.error("Please select a category")
      return
    }

    if (!user) {
      toast.error("You must be logged in to save transactions")
      return
    }

    setIsSaving(true)

    try {
      const tableName = type === "expense" ? "user_expenses" : "user_incomes"

      const insertData: any = {
        user_id: user.id,
        name: description || `${category} ${type}`,
        amount: parseFloat(amount),
        frequency: "one-time",
        created_at: new Date(date).toISOString()
      }

      if (type === "expense") {
        insertData.category = category
      }

      const { error } = await supabase.from(tableName).insert(insertData)

      if (error) {
        console.error("Supabase error:", error)
        toast.error(`Failed to save ${type}: ${error.message}`)
        return
      }

      toast.success(`${type === "expense" ? "Expense" : "Income"} saved successfully!`)
      await refreshTransactions()

      // Reset form
      setScannedReceipt(null)
      setAmount("")
      setDescription("")
      setCategory("")
      setDate("")

      // Go back after short delay
      setTimeout(() => {
        onBack()
      }, 500)
    } catch (error) {
      console.error("Error saving transaction:", error)
      toast.error("Failed to save transaction. Please try again.")
    } finally {
      setIsSaving(false)
    }
  }

  const handleStatementComplete = () => {
    setShowStatementReview(false)
    setStatementTransactions([])
    onBack()
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 bg-card border-b shadow-sm z-10">
        <div className="max-w-md mx-auto px-4 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            onClick={showStatementReview ? () => setShowStatementReview(false) : onBack}
            disabled={isSaving}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold">
            {showStatementReview ? "Review Transactions" : scannedReceipt ? "Review Receipt" : "Scan Document"}
          </h1>
          <div className="w-10" />
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-6 max-w-md mx-auto pb-20">
        {showStatementReview ? (
          // Statement review flow
          <StatementTransactions
            transactions={statementTransactions}
            onComplete={handleStatementComplete}
          />
        ) : scannedReceipt ? (
          // Receipt edit flow
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <h2 className="text-3xl font-semibold">Review & Save</h2>
              <p className="text-sm text-muted-foreground">
                Verify the extracted details and save
              </p>
            </div>

            {/* Amount */}
            <div className="space-y-2">
              <Label htmlFor="amount">Amount</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="text-2xl font-bold h-16"
                disabled={isSaving}
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="h-12"
                disabled={isSaving}
              />
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Input
                id="category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="h-12"
                disabled={isSaving}
              />
            </div>

            {/* Date */}
            <div className="space-y-2">
              <Label htmlFor="date">Date</Label>
              <Input
                id="date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="h-12"
                disabled={isSaving}
              />
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-2 gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setScannedReceipt(null)}
                disabled={isSaving}
              >
                Scan Again
              </Button>
              <Button
                onClick={handleSaveReceipt}
                disabled={isSaving}
                className="bg-accent hover:bg-accent/90 text-accent-foreground"
              >
                {isSaving ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        ) : (
          // Initial scan flow
          <div className="space-y-4">
            <div className="text-center space-y-2 mb-6">
              <h2 className="text-3xl font-semibold">Upload Document</h2>
              <p className="text-sm text-muted-foreground">
                Take a photo or upload a receipt or bank statement
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                AI will automatically detect the document type
              </p>
            </div>

            <ImageScanner
              categories={[...expenseCategories, ...incomeCategories]}
              onDataParsed={handleDataParsed}
            />
          </div>
        )}
      </div>
    </div>
  )
}
